import { EventProgramLinks } from '@/features/digitalPrograms/EventProgramLinks'
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Calendar, MapPin, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useEvent } from '../hooks/useEvent'
import { TicketTypeSelector } from '../components/TicketTypeSelector'
import { CheckoutForm } from '../components/CheckoutForm'
import type { CheckoutItem } from '../types'
import { withErrorCode } from '@/lib/errors'

export function EventDetailPage() {
  const { slug = '' } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { event, ticketTypes, sharedRemainingSeats, loading, error } = useEvent(slug)

  const [selectedItems, setSelectedItems] = useState<CheckoutItem[]>([])
  const [checkoutOpen, setCheckoutOpen] = useState(false)

  const hasSelection = selectedItems.some((i) => i.quantity > 0)

  function handleCheckoutSuccess(orderId: string, receiptToken: string) {
    setCheckoutOpen(false)
    const params = new URLSearchParams({ orderId, receiptToken })
    void navigate(`/tickets/success?${params.toString()}`)
  }

  if (loading) {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-12 space-y-6">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="aspect-video w-full rounded-xl" />
        <Skeleton className="h-10 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  if (error || !event) {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-20 text-center">
        <p className="text-muted-foreground">
          {error ?? withErrorCode('Event not found.', 'ticketing/event-not-found')}
        </p>
        <Button variant="ghost" className="mt-4" onClick={() => void navigate('/events')}>
          ← Back to Events
        </Button>
      </div>
    )
  }

  const formattedDate = event.eventDate.toDate().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

  const formattedTime = event.eventDate.toDate().toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })

  return (
    <div className="container mx-auto max-w-3xl px-4 py-12 space-y-8">
      <Button variant="ghost" size="sm" onClick={() => void navigate('/events')}>
        <ArrowLeft className="mr-1.5 h-4 w-4" />
        All Events
      </Button>

      <EventProgramLinks eventId={event.id} />

      {event.coverUrl && (
        <div className="aspect-video w-full overflow-hidden rounded-xl bg-muted">
          <img src={event.coverUrl} alt={event.title} className="h-full w-full object-cover" />
        </div>
      )}

      <div className="space-y-3">
        <h1 className="text-3xl font-bold sm:text-4xl">{event.title}</h1>

        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Calendar className="h-4 w-4" />
            <span>
              {formattedDate} · {formattedTime}
            </span>
          </div>
          {event.doorTime && <span className="text-muted-foreground/70">{event.doorTime}</span>}
          <div className="flex items-center gap-1.5">
            <MapPin className="h-4 w-4" />
            <span>{event.venue}</span>
          </div>
        </div>

        <p className="text-base leading-relaxed whitespace-pre-wrap">{event.description}</p>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Tickets</h2>
        {event.publicSalesEnabled === false ||
        (event.productionId && event.publicSalesEnabled !== true) ? (
          <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
            Public tickets will open after the participant roster is confirmed.
          </div>
        ) : (
          <TicketTypeSelector
            ticketTypes={ticketTypes}
            sharedCapacity={event.capacity}
            sharedRemainingSeats={sharedRemainingSeats}
            value={selectedItems}
            onChange={setSelectedItems}
          />
        )}

        {ticketTypes.length > 0 && (
          <Button
            className="w-full"
            size="lg"
            disabled={!hasSelection}
            onClick={() => setCheckoutOpen(true)}
          >
            {hasSelection ? 'Get Tickets' : 'Select Tickets to Continue'}
          </Button>
        )}
      </div>

      <Sheet open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <SheetContent className="overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Checkout — {event.title}</SheetTitle>
          </SheetHeader>
          <div className="mt-6">
            {checkoutOpen && (
              <CheckoutForm
                eventId={event.id}
                items={selectedItems}
                onSuccess={handleCheckoutSuccess}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}
